import { Fragment, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Divider, Icon, Text } from '@ui-kitten/components';
import lodashFilter from 'lodash/filter';
import lodashIsEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import Button from '../../Components/Button';
import constants from '../../Config/constants';
import { getCartTerm } from '../../Helper/RemoteConfig';
import useMealPlan from '../../Hooks/useMealPlan';
import { checkout } from '../../RTK/defaultValues';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';
import { SECTION_TITLE_LIGHT } from '../../Theme/typographyProps';

import useCheckoutHandler from './controller/useCheckoutHandler';
import checkoutStyles from './styles';

const vehicleList = [
  { label: 'Car', value: 'car', icon: 'car' },
  { label: 'Motorcycle', value: 'motorcycle', icon: 'motorcycle' },
];

function VehicleOptions({ disabled, hasBulk }) {
  const { isMealPlan } = useMealPlan();
  const { isDelivery, formVehicleOptions, onUpdateCheckoutDetails } =
    useCheckoutHandler();

  const filteredVehicleList = useMemo(() => {
    if (hasBulk) return lodashFilter(vehicleList, { value: 'car' });
    return vehicleList;
  }, [hasBulk]);

  // clear selected value upon closing the checkout page
  useEffect(() => {
    return () => {
      onUpdateCheckoutDetails(checkout.keys.VEHICLE_OPTIONS, undefined);
    };
  }, [onUpdateCheckoutDetails]);

  const _onVehiclePressed = (v) => () => {
    if (v.value === formVehicleOptions?.value) {
      return; // if already selected just return
    }
    onUpdateCheckoutDetails(checkout.keys.VEHICLE_OPTIONS, v);
  };

  if (!isDelivery && !isMealPlan) return null;

  return (
    <Fragment>
      <View style={ThemeStyle.spacingTopMedium}>
        <View style={checkoutStyles.webBox}>
          <Text
            style={ThemeStyle.spacingBottomMedium}
            {...(constants.isWeb ? { category: 's2' } : SECTION_TITLE_LIGHT)}
          >
            Delivery Vehicle Options
          </Text>

          <View
            style={[
              ThemeStyle.flexDirectionRowCenter,
              ThemeStyle.spacingBottomMedium,
            ]}
          >
            {filteredVehicleList.map((v, i) => (
              <Button
                key={i}
                onPress={_onVehiclePressed(v)}
                style={ThemeStyle.spacingRightMedium}
                disabled={disabled}
                plain
              >
                <View
                  style={[
                    styles.container,
                    lodashIsEqual(v, formVehicleOptions) &&
                      styles.containerActive,
                  ]}
                >
                  <Icon
                    name={v.icon}
                    pack="fa"
                    fill={ThemeColor.white}
                    style={styles.icon}
                  />

                  <Text category="p2" appearance="alternative">
                    {v.label}
                  </Text>
                </View>
              </Button>
            ))}
          </View>

          {hasBulk && (
            <Text
              category="p2"
              style={[ThemeStyle.noticeText, ThemeStyle.spacingBottomMedium]}
            >
              You currently have bulk items in your {getCartTerm()} that
              requires car delivery
            </Text>
          )}
        </View>
      </View>

      {!constants.isWeb && <Divider style={ThemeStyle.divider} />}
    </Fragment>
  );
}

const styles = StyleSheet.create({
  container: {
    ...ThemeStyle.alignItemsCenter,
    ...ThemeStyle.justifyContentCenter,
    backgroundColor: ThemeColor.gray,
    borderRadius: 10,
    width: 100,
    height: 70,
  },
  containerActive: {
    backgroundColor: ThemeColor.green,
  },
  icon: {
    ...ThemeStyle.spacingBottomSmall,
    width: 20,
    height: 20,
  },
});

VehicleOptions.propTypes = {
  disabled: PropTypes.bool,
  hasBulk: PropTypes.bool,
};

export { vehicleList };
export default VehicleOptions;
